import { ChevronDown, ChevronRight } from "lucide-react";
import type { BoardCard, BoardCellAggregate, BoardRowAggregate, Stage } from "@salesboard/contracts";
import type { BoardDensity } from "./useBoardDensity";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { formatCurrencyCompact } from "@/lib/format";
import { BoardCell } from "./BoardCell";
import { groupIntoCells } from "./order";
import { useCollapsedStages } from "./useCollapsedStages";

/**
 * The grid: one row per active stage, four quarter columns.
 *
 * Plain CSS Grid with cards as ordinary flow children of their cell — no absolute
 * positioning and no offset arithmetic, because a card occupies exactly one cell and never
 * spans. Flow layout gives the vertical stacking a many-cards-per-cell board needs for
 * free, and DOM order equals reading order, which the keyboard and screen-reader story
 * depend on.
 *
 * Sticky headers are pure CSS inside one scroll container: quarter header on top, stage
 * labels on the left, the corner pinned to both.
 *
 * Each stage row's header is also its collapse toggle — the closed rows start shut, the open
 * pipeline gets the vertical space, and a collapsed row keeps its per-cell aggregates and
 * stays droppable. See `useCollapsedStages` for where that state lives and why.
 */
export function BoardGrid({
  stages,
  cards,
  cells,
  rowTotals = [],
  year,
  editable,
  density = "normal",
  currentUserId,
  isHeadOfSales,
  pending,
  onOpenCard,
  onHoverCard,
  onBlockedDrag,
}: {
  stages: Stage[];
  cards: BoardCard[];
  cells: BoardCellAggregate[];
  /**
   * One stage's count and value, server-computed — see `boardRowAggregateSchema`. Optional
   * with an empty-array default so a fixture built before this aggregate existed still
   * renders (a stage simply shows no total), rather than throwing on a missing prop.
   */
  rowTotals?: BoardRowAggregate[];
  year: number;
  editable: boolean;
  /** Cards, or the condensed chips. Passed straight through to each cell. */
  density?: BoardDensity;
  currentUserId: string | undefined;
  isHeadOfSales: boolean;
  /** The one card being moved, drawn at its target rather than where the cache has it. */
  pending?: { id: string; stageId: string; quarter: number };
  onOpenCard: (card: BoardCard) => void;
  onHoverCard?: (card: BoardCard) => void;
  onBlockedDrag: (card: BoardCard) => void;
}) {
  const t = useT();
  // Derived order, never manual — see order.ts, which is where that decision is tested.
  const byCell = groupIntoCells(cards, pending);
  const rows = useCollapsedStages();

  const aggregate = new Map(cells.map((cell) => [`${cell.stageId}:${cell.quarter}`, cell]));
  const rowAggregate = new Map(rowTotals.map((row) => [row.stageId, row]));
  const quarters = [1, 2, 3, 4] as const;

  return (
    // `bg-background`: the board is a data surface like the table it otherwise resembles
    // (see `DataTable`'s own `bg-card`), so it stays opaque over the shell's page-scale
    // brand wash rather than showing gradient through the gaps between cards.
    <div
      role="region"
      aria-label={t("board.region", year)}
      className="bg-background border-border overflow-auto rounded-lg border"
    >
      <div className="grid min-w-[56rem] grid-cols-[max-content_repeat(4,minmax(13rem,1fr))]">
        {/* Corner: pinned to both axes, so it sits above both sticky bands. z-30 — see the
            z-index ladder in AppLayout.tsx's header: below the app header (z-40) and every
            Radix overlay (z-50+), above the quarter headers (z-20) and stage rows (z-10)
            it pins over. */}
        <div className="bg-background sticky top-0 left-0 z-30 border-r border-b px-3 py-2" />
        {quarters.map((quarter) => (
          <div
            key={quarter}
            className="bg-background text-muted-foreground board-type-secondary sticky top-0 z-20 border-r border-b px-3 py-2 font-medium last:border-r-0"
          >
            {t("board.quarterHeader", quarter)} <span className="opacity-60">{year}</span>
          </div>
        ))}

        {stages.map((stage) => {
          const collapsed = rows.isCollapsed(stage);
          const Chevron = collapsed ? ChevronRight : ChevronDown;
          // Server-computed — never summed from `cells` here — and shown whether the row
          // is expanded or collapsed, which is the point of showing it on the row at all:
          // a collapsed row hides the cards it holds, never what they are worth.
          const rowTotal = rowAggregate.get(stage.id);

          return (
            <div key={stage.id} className="contents">
              {/* The whole row header is the toggle: a chevron-sized target on a surface
                  meant to be read from across a meeting room is hostile, and a real button
                  brings `aria-expanded` and keyboard reach with it.
                  z-10 — see the z-index ladder in AppLayout.tsx's header: below the
                  quarter headers and corner it shares this axis with. */}
              <div className="bg-background sticky left-0 z-10 flex min-w-40 border-r border-b">
                <button
                  type="button"
                  aria-expanded={!collapsed}
                  onClick={() => rows.toggle(stage)}
                  // `items-start`, not `items-center`: a tall row (many cards stacked in a
                  // cell) stretches this button to the row's full height, and a centred
                  // label used to float in the middle of it instead of reading as the
                  // row's heading.
                  className="hover:bg-accent/50 focus-visible:ring-ring flex w-full items-start gap-1.5 px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:-outline-offset-2"
                >
                  <span className="min-w-0">
                    {/* The stage name has to stay the first text in the button — the
                        e2e `rowHeader` helper matches the accessible name by
                        `^<stage name>\b` — so the chevron trails it on the same line
                        rather than leading the row as it used to. Smaller than the label
                        it toggles: on a row read from across a meeting room the name is
                        the fact, the chevron only a hint that it opens. */}
                    <span className="flex items-center gap-1">
                      <span className="board-type-primary font-medium">{stage.name}</span>
                      <Chevron className="text-muted-foreground h-3 w-3 shrink-0" aria-hidden />
                    </span>
                    {/* The type is a cue for a closed stage whose *name* doesn't say so
                        (stages are renameable). Under "Closed Won" it is pure repetition. */}
                    {stage.closedType !== "open" && !stage.name.toLowerCase().includes(stage.closedType) && (
                      <span className="text-muted-foreground board-type-meta block uppercase">
                        {t(`status.${stage.closedType}`)}
                      </span>
                    )}
                    {/* Count and value on their own lines rather than joined by "·" on
                        one: the row can be several cards deep and this header is read
                        top to bottom, not scanned left to right like a table cell.

                        An empty row says nothing at all (2026-08-26). "0 opportunities /
                        CZK 0" is two lines restating what the row's own emptiness already
                        shows, on every stage nobody is working this year — and six of them
                        down the side of a board is most of what the eye lands on. The name
                        stays, because the row is still a place a card can be dropped. */}
                    {rowTotal && rowTotal.count > 0 && (
                      <>
                        <span className="text-muted-foreground board-type-meta block">
                          {t("count.opportunities", rowTotal.count)}
                        </span>
                        <span className="text-muted-foreground board-type-meta block">
                          {formatCurrencyCompact(rowTotal.totalNip)}
                        </span>
                      </>
                    )}
                  </span>
                </button>
              </div>

              {quarters.map((quarter) => (
                <BoardCell
                  key={`${stage.id}:${quarter}`}
                  stageId={stage.id}
                  stageName={stage.name}
                  quarter={quarter}
                  density={density}
                  cards={byCell.get(`${stage.id}:${quarter}`) ?? []}
                  aggregate={aggregate.get(`${stage.id}:${quarter}`)}
                  canDrop={editable}
                  // A collapsed row's cells stay droppable, and a drop on one behaves
                  // exactly as it would expanded. The row deliberately does not
                  // auto-expand: reshuffling the grid under the cursor mid-drag is the
                  // jank that would cost more than it buys.
                  collapsed={collapsed}
                  currentUserId={currentUserId}
                  isHeadOfSales={isHeadOfSales}
                  pendingCardId={pending?.id}
                  onOpenCard={onOpenCard}
                  onHoverCard={onHoverCard}
                  onBlockedDrag={onBlockedDrag}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The loading state renders the grid's structure with placeholder cells — never an empty
 * grid, which on a sales board would read as "no pipeline".
 */
export function BoardGridSkeleton({ rows = 6 }: { rows?: number }) {
  const t = useT();

  return (
    <div className="bg-background border-border overflow-hidden rounded-lg border" aria-busy="true">
      <div className="grid min-w-[56rem] grid-cols-[max-content_repeat(4,minmax(13rem,1fr))]">
        <div className="border-r border-b px-3 py-2" />
        {[1, 2, 3, 4].map((q) => (
          <div key={q} className="text-muted-foreground border-r border-b px-3 py-2 text-xs last:border-r-0">
            {t("board.quarterHeader", q)}
          </div>
        ))}
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className="contents">
            <div className="border-r border-b px-3 py-4">
              <div className="bg-muted h-4 w-28 animate-pulse rounded" />
            </div>
            {[1, 2, 3, 4].map((q) => (
              <div key={q} className={cn("space-y-1.5 border-r border-b p-1.5 last:border-r-0")}>
                <div className="bg-muted h-14 animate-pulse rounded-md" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
